import {
    styled,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableRow,
    TableContainer,
    Typography,
} from '@mui/material';

import {
    formationWithOptionsMapping,
    formationWithoutOptionsMapping,
    masterMapping,
} from '../../constants';
import { keysOf } from '../../../lib/keysOf';
import {
    formationsWithOptionsSummaryType,
    formationsWithoutOptionsSummaryType,
    fundingType,
    masterSlugType,
} from '../../types';
import { formationContinuePricesHandler } from '../lib/formationContinuePricesHandler';

type rowType = { title: string; price?: number; priceLabel: string };

function SummaryTable(props: {
    formationsWithOptionsSummary: formationsWithOptionsSummaryType;
    formationsWithoutOptionsSummary: formationsWithoutOptionsSummaryType;
    selectedMaster: masterSlugType | undefined;
    funding: fundingType;
}) {
    const rows = computeRowsContent();
    const totalPrice = rows.reduce((acc, row) => {
        if (row.price !== undefined) {
            return acc + row.price;
        }
        return acc;
    }, 0);
    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <HeadCellContent>Intitulé de la formation</HeadCellContent>
                        </TableCell>
                        <TableCell>
                            <HeadCellContent>Prix</HeadCellContent>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => (
                        <TableRow key={`row-${index}`}>
                            <TableCell>
                                <Typography>{row.title}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography>{row.priceLabel}</Typography>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TableCell>
                            <Typography>Total</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography>{totalPrice} €</Typography>
                        </TableCell>
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    );

    function computeRowsContent() {
        let rows: rowType[] = [];
        for (const formationsWithOptionsSlug of keysOf(props.formationsWithOptionsSummary)) {
            const summary = props.formationsWithOptionsSummary[formationsWithOptionsSlug];
            if (summary.option === undefined) {
                continue;
            }
            const formationInfo = formationWithOptionsMapping[formationsWithOptionsSlug];
            const title = formationInfo.title;

            switch (summary.option) {
                case 'inter':
                    const price = formationContinuePricesHandler.computeFormationWithOptionsPrice(
                        formationsWithOptionsSlug,
                        {
                            fundingKind: props.funding.kind,
                        },
                    );
                    rows.push({
                        title,
                        price,
                        priceLabel: `${price} €`,
                    });

                    break;
                case 'intra':
                    rows.push({
                        title,
                        priceLabel: 'sur devis',
                    });
                    break;
            }
        }

        for (const formationsWithoutOptionsSlug of keysOf(props.formationsWithoutOptionsSummary)) {
            const summary = props.formationsWithoutOptionsSummary[formationsWithoutOptionsSlug];
            if (!summary.isSelected) {
                continue;
            }
            const formationWithoutOptions =
                formationWithoutOptionsMapping[formationsWithoutOptionsSlug];
            rows.push({
                title: formationWithoutOptions.title,
                priceLabel: 'sur devis',
            });
        }

        if (props.selectedMaster !== undefined) {
            const masterInfo = masterMapping[props.selectedMaster];
            const price = formationContinuePricesHandler.computeMasterPrice(props.selectedMaster);
            rows.push({
                title: masterInfo.title,
                priceLabel: `${price} €`,
                price,
            });
        }

        const discount = formationContinuePricesHandler.computeDiscountPrice(
            keysOf(props.formationsWithOptionsSummary).filter(
                (slug) => props.formationsWithOptionsSummary[slug].option !== undefined,
            ),
            { fundingKind: props.funding.kind },
        );

        if (discount > 0) {
            rows.push({
                title: 'Réduction pour inscription groupée',
                priceLabel: `- ${discount} €`,
                price: -discount,
            });
        }

        return rows;
    }
}

const HeadCellContent = styled(Typography)(({ theme }) => ({ fontWeight: 'bold' }));

export { SummaryTable };
